import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { tca } from "@/api/tca";
import { Button, Card, CardBody, EmptyState, Select } from "@/components/ui";

type View = "venue" | "symbol" | "hour";

/**
 * Transaction-cost analysis — execution slippage and latency bucketed by
 * venue, symbol, or hour-of-day. Read-only operational analytics.
 */
export function AdminTcaPage() {
  const [view, setView] = useState<View>("venue");
  const [days, setDays] = useState(7);

  const q = useQuery({
    queryKey: ["tca", view, days],
    queryFn: () =>
      view === "venue" ? tca.byVenue(days) : view === "symbol" ? tca.bySymbol(days) : tca.byHour(days),
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-base font-semibold text-term-fg">Transaction cost analysis</h1>
        <div className="flex items-center gap-2">
          <div className="flex gap-1">
            {(["venue", "symbol", "hour"] as View[]).map((v) => (
              <Button key={v} variant={view === v ? "primary" : "ghost"} onClick={() => setView(v)}>
                By {v}
              </Button>
            ))}
          </div>
          <div className="w-28">
            <Select value={String(days)} onChange={(e) => setDays(parseInt(e.target.value, 10))}>
              {[1, 7, 30, 90].map((d) => (
                <option key={d} value={d}>
                  {d}d
                </option>
              ))}
            </Select>
          </div>
        </div>
      </div>

      <Card>
        <CardBody className="p-0">
          {q.isLoading ? (
            <div className="p-3 text-sm text-term-mute">Loading…</div>
          ) : !q.data || q.data.length === 0 ? (
            <EmptyState title="No fills in window" description="Execution data appears once trades settle." />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">{view}</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Fills</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Notional</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Avg slip bps</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Med slip bps</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">p99 slip bps</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Avg lat ms</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">p99 lat ms</th>
                </tr>
              </thead>
              <tbody>
                {q.data.map((r) => (
                  <tr key={r.bucket} className="border-t border-term-border hover:bg-term-elev">
                    <td className="px-3 py-1.5 font-mono text-2xs text-term-fg">{r.bucket}</td>
                    <td className="px-3 py-1.5 text-right tnum text-term-dim">{r.fill_count}</td>
                    <td className="px-3 py-1.5 text-right tnum text-term-dim">
                      {r.notional_total.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </td>
                    <td className="px-3 py-1.5 text-right tnum text-term-fg">{r.avg_slippage_bps.toFixed(2)}</td>
                    <td className="px-3 py-1.5 text-right tnum text-term-dim">{r.median_slippage_bps.toFixed(2)}</td>
                    <td className="px-3 py-1.5 text-right tnum text-term-dim">{r.p99_slippage_bps.toFixed(2)}</td>
                    <td className="px-3 py-1.5 text-right tnum text-term-dim">{r.avg_latency_ms.toFixed(0)}</td>
                    <td className="px-3 py-1.5 text-right tnum text-term-dim">{r.p99_latency_ms.toFixed(0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
